import React from 'react';
import { CurrencyUtils, TranslationUtils } from '../../utils';
import './ReceiptMor.scss';
import { ReceiptLineAddNight, ReceiptLineOccupancy, ReceiptLineSummary, ReceiptTotalTitle } from '../../components';
import { OccupancyCounts } from '../../types/occupancy';
import { TripAdjustmentResponse } from 'guest-data-types-ts-lib/types';
import { ReceiptLineExtendStay } from './ReceiptLineExtendStay';
import { ReceiptLinePetAdjustment } from './ReceiptLinePetAdjustment';
import { DiscountDetail } from '../../types/receipt.type';
import { ReceiptLineLateCheckOutAdjustment } from './ReceiptLineLateCheckOutAdjustment';
import { ReceiptLinePoolHeatAdjustment } from './ReceiptLinePoolHeatAdjustment';

interface ReceiptDetailsAdjustmentProps {
    discount_details?: DiscountDetail;
    occupancy_details?: { initialOccupancy: OccupancyCounts | null; occupancyCounts: OccupancyCounts | null };
    preview: TripAdjustmentResponse;
    locale: string;
}

export const ReceiptDetailsAdjustment: React.FC<ReceiptDetailsAdjustmentProps> = (props) => {
    const { preview, locale, discount_details, occupancy_details } = props;
    const { adjustment, delta, converted_delta, detail } = preview;
    const shouldShowPreviousBalance =
        (adjustment.name === 'adjustOccupancyAndPets' || adjustment.name === 'updatePetAdjustment') && detail!.previous_balance > 0;
    const isAddNightFlow = adjustment.name === 'addNight' || adjustment.name === 'tripAddNight';

    return (
        <div id={'main-receipt-details-adjustment'} className={'p-2 price-detail-block'}>
            <div tabIndex={0}>
                <h3 className={'sr-only'}>{TranslationUtils.get('payment_form_pricing_details')}</h3>
            </div>
            <h4 className={'p-2 muted type-body-small font-weight-bold price-breakdown-header text-uppercase'} />
            <div className="price-breakdown-toggle">
                {isAddNightFlow && <ReceiptLineAddNight preview={preview} discount_details={discount_details} locale={locale} />}
                {adjustment.name === 'adjustOccupancyAndPets' && (
                    <ReceiptLineOccupancy occupancy_details={occupancy_details} preview={preview} locale={locale} />
                )}
                {adjustment.name === 'extendedStay' && <ReceiptLineExtendStay preview={preview} locale={locale} />}
                {adjustment.name === 'lateCheckout' && <ReceiptLineLateCheckOutAdjustment preview={preview} locale={locale} />}
                {adjustment.name === 'updatePetAdjustment' && occupancy_details?.initialOccupancy && occupancy_details?.occupancyCounts && (
                    <ReceiptLinePetAdjustment occupancy_details={occupancy_details} preview={preview} locale={locale} />
                )}
                {adjustment.name === 'poolHeat' && <ReceiptLinePoolHeatAdjustment preview={preview} locale={locale} />}
            </div>
            {adjustment.name !== 'poolHeat' && (
                <ReceiptLineSummary
                    summaryTitle={TranslationUtils.get('fees')}
                    summaryValue={CurrencyUtils.formatCurrency(delta?.fees || 0, delta.currency)}
                    currency={delta.currency}
                    locale={locale}
                />
            )}
            <ReceiptLineSummary
                summaryTitle={TranslationUtils.get('taxes')}
                summaryValue={CurrencyUtils.formatCurrency(delta?.taxes || 0, delta.currency)}
                currency={delta.currency}
                locale={locale}
            />

            {shouldShowPreviousBalance && (
                <>
                    <hr className={'detail-separator'} />
                    {
                        <ReceiptLineSummary
                            summaryTitle={TranslationUtils.get('previous_balance')}
                            summaryValue={CurrencyUtils.formatCurrency(detail!.previous_balance, delta.currency)}
                            currency={delta.currency}
                            locale={locale}
                        />
                    }
                </>
            )}
            {isAddNightFlow && <ReceiptTotalTitle summary={delta} convertedSummary={converted_delta} locale={locale} />}
            {adjustment.name === 'adjustOccupancyAndPets' && (
                <ReceiptTotalTitle
                    summary={{
                        total: detail!.total,
                        currency: delta.currency,
                    }}
                    convertedSummary={converted_delta}
                    locale={locale}
                />
            )}
            {(adjustment.name === 'extendedStay' ||
                adjustment.name === 'updatePetAdjustment' ||
                adjustment.name === 'lateCheckout' ||
                adjustment.name == 'poolHeat') && <ReceiptTotalTitle summary={delta} locale={locale} />}
        </div>
    );
};
